/* eslint-disable react/prop-types */
import dayjs from 'dayjs'
import { isNil } from 'lodash'
import React, { useEffect, useState } from 'react'

import { fetchEmandateStatus } from 'api'

import * as styles from './styles'

import { TitleDescription } from '../../../../../components/TitleDescription'

const formatRupees = (paise) => {
  if (isNil(paise)) return '—'
  return `₹${Number(paise / 100).toLocaleString('en-IN')}`
}

const formatAmount = (rupees) =>
  `₹${Number(rupees || 0).toLocaleString('en-IN')}`

/**
 * Bookings this parent has that have not finished settling.
 *
 * Shown so the team can see a booking is already under way before making
 * another one. Two states matter and they need reading differently:
 *
 *   debit in progress — Razorpay is collecting. Do not book again.
 *   no debit started  — the cart was created but the charge never began,
 *                       which is what a mandate that cannot be charged looks
 *                       like. Booking again just adds another dead invoice;
 *                       the mandate needs fixing instead.
 *
 * The list is what stops the retry loop: on prod one parent reached four
 * invoices for the same month across two days, none of which started a debit,
 * because nothing on this screen said so.
 */
const OpenDebits = ({ debits }) => {
  if (!debits?.length) return null
  const total = debits.reduce((sum, d) => sum + Number(d.amount_rupees || 0), 0)
  const stalled = debits.filter((d) => d.state !== 'debit in progress')
  const allStalled = stalled.length === debits.length
  return (
    <styles.OpenDebits>
      <styles.OpenDebitsHeading $stalled={stalled.length > 0}>
        {debits.length === 1
          ? '1 booking not settled'
          : `${debits.length} bookings not settled · ${formatAmount(total)}`}
      </styles.OpenDebitsHeading>
      {debits.map((debit) => {
        const isStalled = debit.state !== 'debit in progress'
        return (
          <styles.OpenDebitRow key={debit.invoice_uuid} $stalled={isStalled}>
            <styles.OpenDebitTop>
              <styles.OpenDebitAmount>
                {formatAmount(debit.amount_rupees)}
              </styles.OpenDebitAmount>
              <styles.OpenDebitState $stalled={isStalled}>
                {debit.state}
              </styles.OpenDebitState>
            </styles.OpenDebitTop>
            <styles.OpenDebitMeta>
              {[
                debit.subjects,
                debit.created
                  ? dayjs(debit.created).format('D MMM, HH:mm')
                  : null
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </styles.OpenDebitMeta>
          </styles.OpenDebitRow>
        )
      })}
      <styles.OpenDebitWarning $stalled={stalled.length > 0}>
        {allStalled
          ? 'No debit has started. Booking again will not charge — check the mandate.'
          : stalled.length
          ? 'Some bookings never started a debit. Check the mandate before rebooking.'
          : 'Collection in progress — do not book again.'}
      </styles.OpenDebitWarning>
    </styles.OpenDebits>
  )
}

/**
 * Autopay card, shown beside Wallet.
 *
 * The two numbers the centre team needs before touching a booking: the mandate's
 * ceiling (what a debit is allowed to collect) and when it was authorised. A cap
 * lower than the parent's monthly bill is the reason "book with e-mandate" will
 * be missing from the cart, so it is worth reading here rather than guessing.
 */
export const EMandate = (props) => {
  const { selectedParentDetails, onNeedsRoom } = props
  const [mandate, setMandate] = useState(null)
  const [loading, setLoading] = useState(true)

  const openDebits = mandate?.open_debits
  // The card sits in a fixed-height grid slot. One row per unsettled booking
  // overflows it, so tell the wrapper to grow — otherwise the rows are clipped
  // and a parent with four dead invoices looks identical to one with none.
  useEffect(() => {
    onNeedsRoom?.(Boolean(openDebits?.length))
  }, [openDebits, onNeedsRoom])

  const userUuid = selectedParentDetails?.user?.uuid

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!userUuid) {
        setLoading(false)
        return
      }
      setLoading(true)
      const data = await fetchEmandateStatus({ userUuid })
      if (!cancelled) {
        setMandate(data)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userUuid])

  if (loading) {
    return (
      <styles.Body>
        <styles.ColumnFlexCenter>
          <TitleDescription
            heading="Status"
            size="medium"
            align="center"
            description="loading…"
          />
        </styles.ColumnFlexCenter>
      </styles.Body>
    )
  }

  const activeToken = mandate?.active_token
  const isEnabled = Boolean(mandate?.enabled && activeToken)

  if (!isEnabled) {
    // A revoked/failed row is still worth showing: it tells the team the parent
    // tried, rather than never having been offered autopay.
    const lastAttempt = mandate?.history?.[0]
    return (
      <styles.Body>
        <styles.ColumnFlexCenter>
          <TitleDescription
            heading="Status"
            size="medium"
            align="center"
            description={<styles.Inactive>not enabled</styles.Inactive>}
          />
          {lastAttempt ? (
            <TitleDescription
              heading="Last attempt"
              size="small"
              align="center"
              description={`${lastAttempt.status} on ${dayjs(
                lastAttempt.created
              ).format('D MMM, YYYY')}`}
            />
          ) : null}
          <OpenDebits debits={openDebits} />
        </styles.ColumnFlexCenter>
      </styles.Body>
    )
  }

  const authorisedOn = activeToken.authorised_on || activeToken.created

  return (
    <styles.Body>
      <styles.ColumnFlexCenter>
        <TitleDescription
          heading="Max amount"
          size="medium"
          align="center"
          description={
            <styles.Amount>
              {formatRupees(activeToken.max_amount)}
            </styles.Amount>
          }
        />
        <TitleDescription
          heading="Enabled on"
          size="small"
          align="center"
          description={
            authorisedOn ? dayjs(authorisedOn).format('D MMM, YYYY') : '—'
          }
        />
        {activeToken.method ? (
          <TitleDescription
            heading="Method"
            size="small"
            align="center"
            description={activeToken.method}
          />
        ) : null}
        <OpenDebits debits={openDebits} />
      </styles.ColumnFlexCenter>
    </styles.Body>
  )
}
